import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { ContentReportsProxy } from '../services/catalogServiceAPI/contentReportsProxy';
import { NotificationsProxy } from '../services/catalogServiceAPI/notificationsProxy';
import { openReportsStore } from './openReportsStore';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'all', label: 'All' },
];

const REASON_LABELS = {
    inappropriate: 'Inappropriate or offensive',
    spam: 'Spam or misleading',
    other: 'Something else',
};

const renderingImageUrl = (id) => `https://designwithfriends.com/renderings/${id}`;
const renderingPageUrl = (id) => `https://designwithfriends.com/render/${id}`;
const designerPageUrl = (id) => `https://designwithfriends.com/designer/${encodeURIComponent(id)}`;

const proxy = new ContentReportsProxy();
const notifications = new NotificationsProxy();

/** What the reporter hears when a report is resolved as "nothing wrong here". */
const REVIEWED_NOTIFICATION = {
    title: 'We Reviewed Your Report',
    body: 'Thank you for being a part of our community. We reviewed your report and found that the reported content does not violate our Terms of Service. If you prefer not to see content from this designer, you can block them at any time.',
};

/**
 * Moderation inbox for in-app content reports (App Store Guideline 1.2): every
 * report a user files from the flag button lands here until it is resolved.
 * This page only tracks the report's lifecycle; "Resolve & notify" also tells
 * the reporter the content was reviewed and found fine.
 */
export function ContentReports() {
    const [status, setStatus] = useState('open');
    const [reports, setReports] = useState(null);

    const load = useCallback(async () => {
        setReports(null);
        openReportsStore.refresh();
        try {
            setReports(await proxy.list(status));
        } catch (e) {
            message.error(`Failed to load reports: ${e.message}`);
            setReports([]);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const setReportStatus = async (id, next) => {
        try {
            const updated = await proxy.setStatus(id, next);
            setReports((rows) => rows.map((r) => (r.id === id ? updated : r)));
            openReportsStore.refresh();
        } catch (e) {
            message.error(`Failed to update report: ${e.message}`);
        }
    };

    const resolveAndNotify = async (report) => {
        try {
            const { delivered } = await notifications.send({ targetUids: [report.reporterId], ...REVIEWED_NOTIFICATION });
            if (!delivered) {
                message.warning('The reporter could not be notified; the report stays open.');
                return;
            }
            message.success('Reporter notified');
        } catch (e) {
            message.error(`Failed to notify the reporter: ${e.message}`);
            return;
        }
        await setReportStatus(report.id, 'resolved');
    };

    const columns = [
        { title: 'Filed', dataIndex: 'createdAt', width: 170, render: (v) => new Date(v).toLocaleString() },
        {
            title: 'Reported',
            dataIndex: 'target',
            width: 140,
            // Renderings have a preview and profiles a link; other target kinds fall back to their type and id.
            render: (target) => target.type === 'rendering' ? (
                <a href={renderingPageUrl(target.id)} target="_blank" rel="noreferrer">
                    <img src={renderingImageUrl(target.id)} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6 }} />
                </a>
            ) : target.type === 'profile' ? (
                <a href={designerPageUrl(target.id)} target="_blank" rel="noreferrer">Designer profile</a>
            ) : (
                <Space direction="vertical" size={0}>
                    <Tag>{target.type}</Tag>
                    <Text type="secondary" copyable style={{ fontSize: 12 }}>{target.id}</Text>
                </Space>
            ),
        },
        { title: 'Reason', dataIndex: 'reason', width: 180, render: (r) => REASON_LABELS[r] || r },
        { title: 'Details', dataIndex: 'details', render: (d) => d || <Text type="secondary">—</Text> },
        {
            title: 'Designer',
            width: 200,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{r.reportedUserName || <Text type="secondary">unknown</Text>}</span>
                    {r.reportedUserId && <Text type="secondary" copyable style={{ fontSize: 12 }}>{r.reportedUserId}</Text>}
                </Space>
            ),
        },
        { title: 'Reporter', dataIndex: 'reporterId', width: 160, render: (v) => <Text type="secondary" copyable style={{ fontSize: 12 }}>{v}</Text> },
        {
            title: 'Context',
            width: 160,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span><Tag>{r.platform}</Tag><Tag>{r.surface}</Tag></span>
                    {r.context?.challengeId && <Text type="secondary" style={{ fontSize: 12 }}>challenge {r.context.challengeId.slice(0, 8)}…</Text>}
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s) => <Tag color={s === 'open' ? 'orange' : 'green'}>{s}</Tag>,
        },
        {
            title: 'Action',
            width: 170,
            align: 'center',
            render: (_, r) => r.status === 'open' ? (
                <Space direction="vertical" size={4}>
                    <Button size="small" type="primary" onClick={() => setReportStatus(r.id, 'resolved')}>Resolve</Button>
                    <Popconfirm
                        title="Resolve and notify the reporter?"
                        description={<div style={{ maxWidth: 360 }}><b>{REVIEWED_NOTIFICATION.title}</b><br />{REVIEWED_NOTIFICATION.body}</div>}
                        okText="Send & resolve"
                        onConfirm={() => resolveAndNotify(r)}
                        disabled={!r.reporterId}
                    >
                        <Button size="small" disabled={!r.reporterId}>Resolve & notify</Button>
                    </Popconfirm>
                </Space>
            ) : (
                <Button size="small" onClick={() => setReportStatus(r.id, 'open')}>Reopen</Button>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 16 }} align="center">
                <Title level={3} style={{ margin: 0 }}>Content reports</Title>
                <Select value={status} options={STATUS_OPTIONS} onChange={setStatus} style={{ width: 140 }} />
                <Button onClick={load}>Refresh</Button>
            </Space>
            {reports === null ? (
                <Spin />
            ) : (
                <Table
                    rowKey="id"
                    dataSource={reports}
                    columns={columns}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: status === 'open' ? 'No open reports 🎉' : 'No reports' }}
                />
            )}
        </div>
    );
}
